import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import { getDocsByIdsChunked } from '../utils/firestoreChunking';
import MealCardList from './MealCardList';
import './MealTile.css';

const MealTile = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [hostedMeals, setHostedMeals] = useState([]);
  const [attendingMeals, setAttendingMeals] = useState([]);
  const [mealsTab, setMealsTab] = useState('hosting');

  useEffect(() => {
    const fetchHostedMeals = async () => {
      if (!currentUser) return;

      try {
        const mealsQuery = query(
          collection(db, 'meals'),
          where('hostId', '==', currentUser.uid),
          where('status', '==', 'active')
        );
        const querySnapshot = await getDocs(mealsQuery);
        const meals = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setHostedMeals(meals);
      } catch (err) {
        console.error('Error fetching hosted meals:', err);
      }
    };

    const fetchAttendingMeals = async () => {
      if (!currentUser) return;

      try {
        const bookingsQuery = query(
          collection(db, 'bookings'),
          where('guestId', '==', currentUser.uid),
          where('status', '==', 'confirmed'),
          where('mealStatus', '==', 'active'),
          where('mealDateTime', '>=', Timestamp.now()),
          orderBy('mealDateTime', 'asc')
        );
        const bookingsSnapshot = await getDocs(bookingsQuery);
        const bookings = bookingsSnapshot.docs.map(doc => doc.data());

        const mealIds = bookings.map(booking => booking.mealId);
        if (mealIds.length === 0) {
          setAttendingMeals([]);
          return;
        }

        // Filtering/sorting already happened server-side on the booking
        // query above; this is only a bounded display-field lookup
        // (title/photoUrl aren't denormalized onto bookings) for the
        // already-filtered set, not the old unbounded hot path.
        const mealDocs = await getDocsByIdsChunked(collection(db, 'meals'), mealIds);
        const mealsById = new Map(mealDocs.map(doc => [doc.id, doc.data()]));
        const meals = bookings
          .map(booking => mealsById.has(booking.mealId)
            ? { id: booking.mealId, ...mealsById.get(booking.mealId) }
            : null)
          .filter(Boolean);
        setAttendingMeals(meals);
      } catch (err) {
        console.error('Error fetching attending meals:', err);
      }
    };

    fetchHostedMeals();
    fetchAttendingMeals();
  }, [currentUser]);

  return (
    <div className="meals-tile">
      <h2 className="meals-tile-title">Meals</h2>
      <div className="meals-toggle">
        <button
          className={`meals-toggle-btn ${mealsTab === 'hosting' ? 'active' : ''}`}
          onClick={() => setMealsTab('hosting')}
        >
          Hosting
        </button>
        <button
          className={`meals-toggle-btn ${mealsTab === 'attending' ? 'active' : ''}`}
          onClick={() => setMealsTab('attending')}
        >
          Attending
        </button>
      </div>

      {mealsTab === 'hosting' && (
        <MealCardList
          meals={hostedMeals}
          onSelect={(meal) => navigate(`/meal-summary/${meal.id}`)}
          emptyMessage="You haven't hosted any meals yet."
        />
      )}

      {mealsTab === 'attending' && (
        <MealCardList
          meals={attendingMeals}
          onSelect={(meal) => navigate(`/meal-summary/${meal.id}`)}
          emptyMessage="You haven't booked any meals yet."
        />
      )}
    </div>
  );
};

export default MealTile;
